import { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  useToast,
  Text,
  FormLabel,
  FormControl,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  VStack,
  Box,
  Icon,
} from "@chakra-ui/react";
import { FiEdit3, FiZap } from "react-icons/fi";
import { BrandButton } from "../BrandButton/BrandButton";
import { getAxiosClient } from "../../services/HttpsService";
import { useDispatch } from "react-redux";
import { fetchApplications } from "../../services/state/applications/applications-async-calls";
import { AppDispatch } from "@/services/state/store";
import { Application } from "@/services/state/applications/applications-interface";
import { UserService } from "../../services/UserService";

export const AddApplicationModal = ({
  isOpen,
  onClose,
  edit = false,
  application,
  onImport,
}: {
  isOpen: boolean;
  onClose: () => void;
  edit?: boolean;
  application?: Application;
  onImport?: (file: File) => Promise<void>;
}) => {
  const initialRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();

  // Manual creation states
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [prefix, setPrefix] = useState("");
  const [loading, setLoading] = useState(false);

  // Prompt-based creation states
  const [prompt, setPrompt] = useState("");
  const [promptLoading, setPromptLoading] = useState(false);

  // Tab state
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (edit && application) {
      setName(application.appName);
      setDescription(application.appDesc);
      setPrefix(application.appPrefix);
    }
  }, [edit, application]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setPrefix("");
    setPrompt("");
    setTabIndex(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const onCreate = async () => {
    if (name !== "") {
      setLoading(true);
      const url = "/api/eza_app_application";
      const data = {
        appName: name,
        appPrefix: prefix,
        appDesc: description,
        createdBy: UserService.getUserEmail(),
      };

      try {
        const response = await getAxiosClient().post(url, data);

        if (response.status === 201) {
          toast({
            title: "Successfully created new application",
            description: "Created new application with entered details",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          handleClose();
          dispatch(fetchApplications());
        } else {
          toast({
            title: "Some error has occurred",
            description: "Please try again",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error creating application:", error);
        toast({
          title: "Error",
          description: "An error occurred while creating new application.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    } else {
      toast({
        title: "Name is required",
        description: "Please enter a name and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onUpdate = async () => {
    if (name !== "") {
      setLoading(true);
      const url = `/api/eza_app_application/${application?.id}`;
      const data = {
        appName: name,
        appDesc: description,
        appPrefix: prefix,
        updatedBy: UserService.getUserEmail(),
      };
      try {
        const response = await getAxiosClient().put(url, data);
        if (response.status === 202) {
          toast({
            title: "Successfully updated application",
            description: "Updated application with entered details",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          handleClose();
          dispatch(fetchApplications());
        } else {
          toast({
            title: "Some error has occurred",
            description: "Please try again",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error updating application:", error);
        toast({
          title: "Error",
          description: "An error occurred while updating application.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    } else {
      toast({
        title: "Name is required",
        description: "Please enter a name and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleGenerateFromPrompt = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Prompt Required",
        description: "Please enter a prompt to generate the application.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setPromptLoading(true);

    try {
      // Fixed API call - using the correct endpoint and payload structure
      const response = await getAxiosClient().post(
        `${process.env.REACT_APP_JSON_GENERATOR_URL}/generate_application/download`,
        {
          user_input: prompt.trim(), // Changed from 'prompt' to 'user_input' to match API
          request_id: Date.now().toString(), // Generate a unique request ID
        },
        {
          responseType: "blob", // Important for handling file downloads
        }
      );

      if (response.status === 200) {
        // Create a File object from the blob response
        const blob = response.data;
        const file = new File([blob], "generated-application.zip", {
          type: "application/zip",
        });

        toast({
          title: "Application Generated Successfully",
          description:
            "Your application has been generated and will now be imported.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Close this modal
        handleClose();

        // Call the import function with the generated file if provided
        if (onImport) {
          await onImport(file);
        }
      }
    } catch (error) {
      console.error("Error generating application:", error);
      toast({
        title: "Generation Failed",
        description:
          "An error occurred while generating the application. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPromptLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      initialFocusRef={initialRef}
      size="xl"
      isCentered
    >
      <ModalOverlay
        zIndex={1}
        backdropFilter="blur(10px)" // ← This will blur the page behind the modal
        bg="rgba(0, 0, 0, 0.4)"
      />
      <ModalContent
        bg="rgba(0, 0, 0, 0.9)"
        backdropFilter="blur(20px)"
        border="1px solid rgba(255, 255, 255, 0.4)"
        borderRadius="20px"
        color="white"
      >
        <ModalCloseButton color="white" />
        <ModalBody>
          {edit ? (
            <>
              <Text fontSize="20px" fontWeight="semibold" my={4} color="white">
                Edit Application
              </Text>
              <FormControl isRequired>
                <FormLabel color="white">Name</FormLabel>
                <Input
                  placeholder="Enter name of the application"
                  focusBorderColor="brand.500"
                  ref={initialRef}
                  fontSize="18px"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  overflowX="auto"
                  isRequired
                  bg="rgba(255, 255, 255, 0.1)"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  color="white"
                  _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                  _focus={{
                    borderColor: "brand.500",
                    boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                  }}
                />
              </FormControl>
              <FormControl mt={3}>
                <FormLabel color="white">Prefix</FormLabel>
                <Input
                  placeholder="Enter prefix of the application"
                  focusBorderColor="brand.500"
                  fontSize="18px"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  overflowX="auto"
                  isRequired
                  bg="rgba(255, 255, 255, 0.1)"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  color="white"
                  _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                  _focus={{
                    borderColor: "brand.500",
                    boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                  }}
                />
              </FormControl>
              <FormControl mt={3}>
                <FormLabel color="white">Description</FormLabel>
                <Input
                  placeholder="Enter description of the application"
                  focusBorderColor="brand.500"
                  fontSize="18px"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  overflowX="auto"
                  bg="rgba(255, 255, 255, 0.1)"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  color="white"
                  _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                  _focus={{
                    borderColor: "brand.500",
                    boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                  }}
                />
              </FormControl>
            </>
          ) : (
            <>
              <Text fontSize="20px" fontWeight="semibold" my={4} color="white">
                Create New Application
              </Text>
              <Tabs
                index={tabIndex}
                onChange={(index) => setTabIndex(index)}
                isFitted
                variant="enclosed"
                colorScheme="brand"
              >
                <TabList borderColor="rgba(255, 255, 255, 0.2)">
                  <Tab
                    color="rgba(255, 255, 255, 0.7)"
                    _selected={{
                      color: "white",
                      background: "linear-gradient(to right, #7652f7, #9500ff)",
                      borderColor: "brand.500",
                    }}
                    _hover={{
                      color: "white",
                      bg: "rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <Icon
                      as={FiEdit3 as React.ElementType}
                      mr={2}
                      color="currentColor"
                    />
                    <Text cursor="pointer">Manual Creation</Text>
                  </Tab>
                  <Tab
                    color="rgba(255, 255, 255, 0.7)"
                    _selected={{
                      color: "white",
                      background: "linear-gradient(to right, #7652f7, #9500ff)",
                      borderColor: "brand.500",
                    }}
                    _hover={{
                      color: "white",
                      bg: "rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <Icon
                      as={FiZap as React.ElementType}
                      mr={2}
                      color="currentColor"
                    />
                    <Text cursor="pointer">Prompt-based Creation</Text>
                  </Tab>
                </TabList>

                <TabPanels>
                  {/* Manual Creation Tab */}
                  <TabPanel px={0}>
                    <VStack spacing={4} align="stretch">
                      <FormControl isRequired>
                        <FormLabel color="white">Name</FormLabel>
                        <Input
                          placeholder="Enter name of the application"
                          focusBorderColor="brand.500"
                          ref={initialRef}
                          fontSize="18px"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          overflowX="auto"
                          isRequired
                          bg="rgba(255, 255, 255, 0.1)"
                          borderColor="rgba(255, 255, 255, 0.2)"
                          color="white"
                          _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                          _focus={{
                            borderColor: "brand.500",
                            boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                          }}
                        />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel color="white">Prefix</FormLabel>
                        <Input
                          placeholder="Enter prefix of the application"
                          focusBorderColor="brand.500"
                          fontSize="18px"
                          value={prefix}
                          onChange={(e) => setPrefix(e.target.value)}
                          overflowX="auto"
                          isRequired
                          bg="rgba(255, 255, 255, 0.1)"
                          borderColor="rgba(255, 255, 255, 0.2)"
                          color="white"
                          _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                          _focus={{
                            borderColor: "brand.500",
                            boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                          }}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel color="white">Description</FormLabel>
                        <Input
                          placeholder="Enter description of the application"
                          focusBorderColor="brand.500"
                          fontSize="18px"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          overflowX="auto"
                          bg="rgba(255, 255, 255, 0.1)"
                          borderColor="rgba(255, 255, 255, 0.2)"
                          color="white"
                          _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                          _focus={{
                            borderColor: "brand.500",
                            boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                          }}
                        />
                      </FormControl>
                    </VStack>
                  </TabPanel>

                  {/* Prompt-based Creation Tab */}
                  <TabPanel px={0}>
                    <VStack spacing={4} align="stretch">
                      <FormControl isRequired>
                        <FormLabel color="white">
                          Application Description
                        </FormLabel>
                        <Textarea
                          placeholder="Describe the application you want to create."
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                          rows={6}
                          resize="vertical"
                          focusBorderColor="brand.500"
                          bg="rgba(255, 255, 255, 0.1)"
                          borderColor="rgba(255, 255, 255, 0.2)"
                          color="white"
                          _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                          _focus={{
                            borderColor: "brand.500",
                            boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                          }}
                        />
                      </FormControl>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {edit ? (
            <BrandButton
              onClick={onUpdate}
              isLoading={loading}
              isDisabled={loading}
            >
              Save Changes
            </BrandButton>
          ) : (
            <>
              {tabIndex === 0 ? (
                <BrandButton
                  onClick={onCreate}
                  isLoading={loading}
                  isDisabled={loading}
                >
                  Create
                </BrandButton>
              ) : (
                <BrandButton
                  leftIcon={<Icon as={FiZap as React.ElementType} />}
                  onClick={handleGenerateFromPrompt}
                  isLoading={promptLoading}
                  isDisabled={!prompt.trim() || promptLoading}
                  loadingText="Generating..."
                >
                  Create
                </BrandButton>
              )}
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
